// EmailMarketingFrame — campaign-grain comparison cards for the Email
// Marketing dashboard section (AA read_client_email — MailChimp / HubSpot
// marketing email / Sendinblue-Brevo).
//
// Comparison cards, NOT a table (Claude Design project
// c98828c8-c6c2-4362-b205-182123906ff0, "HubSpot CRM Extension v2.dc.html",
// Email section, treated as a strong reference — its numbers were rebuilt
// on live data and its shape held up under this build's own independent
// re-pull): every HubSpot email client seen live has 1-2 campaigns in a
// 90-day window (ALIBI Music: "New Releases" + one blank-named row; Tyler
// McBroom: one blank-named row alone) — two rows don't earn a search/filter
// toolbar the way DealTable's dozens of deal rows do. A blank-named
// campaign renders as a full-weight card, not a dimmed or demoted one — see
// EmailCampaignRow's doc comment for why it's relabeled "Unnamed campaign"
// rather than hidden, and why it is never folded into another card even
// when more than one blank-named campaign exists in the same window.
//
// Self-contained: named theme tokens only, same convention as the other
// client-analytics frames in this directory (no chart library needed here —
// no time series at this grain).

import { formatNum } from "@/components/client-analytics/primitives";
import type { EmailCampaignRow } from "@/types/clientAnalytics";

export interface EmailMarketingFrameProps {
  campaigns: EmailCampaignRow[];
}

// Sample data for standalone preview / storybook-style rendering. Mirrors
// the real ALIBI Music shape (2026-08-11 live pull).
export const demoEmailCampaigns: EmailCampaignRow[] = [
  {
    name: "New Releases",
    sent: 30537,
    delivered: 30439,
    opens: 7422,
    clicks: 315,
    open_rate: 24.38,
    click_rate: 1.03,
    unsubscribes: 181,
    bounces: 92,
    bounce_rate: 0.3,
  },
  {
    name: "Unnamed campaign",
    sent: 3624,
    delivered: 3610,
    opens: 836,
    clicks: 18,
    open_rate: 23.16,
    click_rate: 0.5,
    unsubscribes: 34,
    bounces: 14,
    bounce_rate: 0.39,
  },
];

// Exported so EmailMarketingPanel's account-level KPI strip (dashboardPanels.tsx)
// uses the exact same rate-formatting rule as the campaign cards below —
// one function, not two independently-written "%"-suffix formatters that
// could drift (e.g. one gaining formatPct's 0-1-fraction assumption by
// accident later).
export function formatRatePct(value: number | null | undefined): string | undefined {
  // AA's own campaign-level open_rate/click_rate/bounce_rate are already
  // percent, 0-100 (confirmed live, 2026-08-11: ALIBI Music's account
  // click_rate 0.978 divides opens/delivered, matching AA's displayed
  // 0.98% exactly) — append "%" directly, never route through
  // primitives.formatPct (which expects a 0-1 fraction and would silently
  // divide this by 100 again).
  if (value === null || value === undefined) return undefined;
  return `${value.toFixed(2)}%`;
}

function CampaignStat({
  label,
  value,
  context,
}: {
  label: string;
  value: string;
  context?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-dash-label uppercase tracking-wide text-muted-foreground truncate">{label}</span>
      <span className="text-dash-subtitle font-bold tabular text-foreground">
        {value}
        {context && <span className="ml-1.5 text-dash-context font-medium text-muted-foreground">{context}</span>}
      </span>
    </div>
  );
}

function EmailCampaignCard({ campaign }: { campaign: EmailCampaignRow }) {
  return (
    <div className="flex-1 min-w-[260px] flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
      <div className="text-dash-subtitle font-semibold text-foreground truncate" title={campaign.name}>
        {campaign.name}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <CampaignStat label="Sent" value={formatNum(campaign.sent)} />
        <CampaignStat label="Delivered" value={formatNum(campaign.delivered)} />
        <CampaignStat label="Opens" value={formatNum(campaign.opens)} context={formatRatePct(campaign.open_rate)} />
        <CampaignStat label="Clicks" value={formatNum(campaign.clicks)} context={formatRatePct(campaign.click_rate)} />
        <CampaignStat
          label="Bounces"
          value={formatNum(campaign.bounces)}
          context={formatRatePct(campaign.bounce_rate)}
        />
        <CampaignStat label="Unsubscribes" value={formatNum(campaign.unsubscribes)} />
      </div>
    </div>
  );
}

// campaign.name is not a stable unique key on its own (two independent
// blank-named campaigns both relabel to "Unnamed campaign" and must NOT be
// treated as duplicates or merged — see the module doc comment) — the
// row's position in the array is stable within one render, so it's folded
// into the key alongside the name.
export function EmailMarketingFrame({ campaigns }: EmailMarketingFrameProps) {
  if (campaigns.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-4">
      {campaigns.map((c, i) => (
        <EmailCampaignCard key={`${c.name}-${i}`} campaign={c} />
      ))}
    </div>
  );
}
